import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "../../../i18n";
import { Button } from "../../../components/ui";
import api from "../../../lib/api";
import { queryKeys } from "../../../lib/queryKeys";

/* Zeigt immer auf den neuesten Installer im Update-Bucket. */
const DESKTOP_DOWNLOAD_URL = "https://download.getcustomy.com/Customy-Desktop-Setup.exe";

/* Statisches UI-Mockup: Schachbrett-Kacheln mit Mono-Kürzeln (Design 3c) */
const heroTiles = [
  { label: "AE", tone: "text-primary" },
  { label: "HL", tone: "text-on-surface" },
  { label: "CF", tone: "text-success" },
  { label: "PA", tone: "text-warning" },
];

const HomePage = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { t } = useTranslation();
  const tf = (key, fallback) => (t(key) === key ? fallback : t(key));

  // Version kommt vom Server (Bucket ohne CORS); fehlt sie, bleibt nur der Knopf.
  const { data: desktopVersion } = useQuery({
    queryKey: queryKeys.desktopRelease.all,
    queryFn: () => api.get("/api/public/desktop-release").then((r) => r.data.version),
    staleTime: 5 * 60_000,
    retry: false,
  });

  useEffect(() => {
    if (hash === "#download") document.getElementById("download")?.scrollIntoView();
  }, [hash]);

  const facts = [
    {
      value: tf("marketing.landing.fact1Value", "250"),
      label: tf("marketing.landing.fact1Label", "Designs pro Batch"),
    },
    {
      value: tf("marketing.landing.fact2Value", "5.000"),
      label: tf("marketing.landing.fact2Label", "Grafiken pro Lauf"),
    },
    {
      value: tf("marketing.landing.fact3Value", "1"),
      label: tf("marketing.landing.fact3Label", "Klick bis zum Marktplatz-Push"),
    },
  ];

  const stepFallbacks = [
    {
      kicker: "01 · Nische",
      title: "Markt wählen",
      desc: "Nischen, Keywords und Zeitpläne für wiederkehrende Generierung.",
    },
    {
      kicker: "02 · Design",
      title: "KI generiert Motive",
      desc: "Druckfertige PNGs mit automatischer Qualitätsprüfung, Dark/Light-Varianten und Review.",
    },
    {
      kicker: "03 · Produktbild",
      title: "Mockups als Batch",
      desc: "1 Produktfoto × 250 Designs = 250 Listing-Bilder. Plus Template-Grafiken aus CSV.",
    },
    {
      kicker: "04 · Artikel",
      title: "Varianten & GTINs",
      desc: "Artikel mit Farben, Größen, Preisen, zweisprachigen Texten und GTIN-Vergabe.",
    },
    {
      kicker: "05 · Kanal",
      title: "Push & Verkauf",
      desc: "Amazon, eBay und Kaufland direkt aus Customy — weitere Kanäle geplant. GS1 läuft automatisch.",
    },
  ];

  const steps = stepFallbacks.map((fallback, index) => {
    const n = index + 1;
    return {
      kicker: tf(`marketing.landing.step${n}Kicker`, fallback.kicker),
      title: tf(`marketing.landing.step${n}Title`, fallback.title),
      desc: tf(`marketing.landing.step${n}Desc`, fallback.desc),
    };
  });

  return (
    <div className="overflow-x-hidden">
      {/* Hero */}
      <section
        className="relative"
        style={{
          background:
            "radial-gradient(900px 500px at 75% 0%, rgb(var(--primary) / 0.08), transparent 65%)",
        }}
      >
        <div className="grid lg:grid-cols-2 gap-12 items-center px-6 md:px-14 pt-14 pb-16 lg:pt-[72px] max-w-[1440px] mx-auto">
          {/* Copy */}
          <div>
            <div className="inline-flex items-center gap-2 border border-outline rounded-full px-3.5 py-1.5 text-[11.5px] font-medium text-on-surface-variant">
              <span className="w-1.5 h-1.5 rounded-full bg-success" />
              {tf("marketing.landing.badge", "Für Print-on-Demand-Händler auf Amazon, Etsy & Co.")}
            </div>
            <h1 className="text-[38px] lg:text-[46px] leading-[1.12] font-semibold tracking-[-.025em] text-on-surface mt-5">
              {tf("marketing.landing.heroTitle", "Von der Nische zum fertigen Listing —")}{" "}
              <span className="text-primary">
                {tf("marketing.landing.heroHighlight", "als Batch, nicht als Handarbeit.")}
              </span>
            </h1>
            <p className="text-[15px] lg:text-base leading-relaxed text-on-surface-variant mt-4 max-w-[480px]">
              {tf(
                "marketing.landing.heroDescription",
                "KI-Designs generieren, auf Produktfotos setzen, Artikel mit Varianten und GTINs anlegen und direkt in die Warenwirtschaft exportieren. Einmal konfigurieren — hunderte Produkte auf Knopfdruck.",
              )}
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Button size="lg" onClick={() => navigate("/register")}>
                {tf("marketing.landing.ctaStart", "Kostenlos starten")}
              </Button>
              <Button size="lg" variant="secondary" onClick={() => navigate("/products")}>
                {tf("marketing.landing.ctaFeatures", "Funktionen ansehen")}
              </Button>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-xs text-on-surface-faint">
              {facts.map((fact) => (
                <span key={fact.label} className="inline-flex items-baseline gap-1.5">
                  <span className="font-mono text-[15px] font-semibold text-on-surface">
                    {fact.value}
                  </span>
                  {fact.label}
                </span>
              ))}
            </div>
          </div>

          {/* App-Vignette: Browser-Chrome mit laufendem Batch (statisches DIV-Mockup) */}
          <div
            className="border border-outline-variant bg-surface-container rounded-[14px] shadow-ambient overflow-hidden"
            aria-hidden="true"
          >
            <div className="flex items-center gap-1.5 px-3.5 py-3 border-b border-outline-variant">
              <span className="w-2 h-2 rounded-full bg-on-surface/[.15]" />
              <span className="w-2 h-2 rounded-full bg-on-surface/[.15]" />
              <span className="w-2 h-2 rounded-full bg-on-surface/[.15]" />
              <span className="ml-2.5 font-mono text-[10.5px] text-on-surface-faint">
                customy.app/mockups
              </span>
            </div>
            <div className="p-4 sm:p-[18px] flex flex-col gap-3">
              <div className="flex items-center gap-2.5">
                <span className="text-[13px] font-semibold text-on-surface">
                  {tf("marketing.landing.vignetteTitle", "Mockup-Batch läuft")}
                </span>
                <span className="font-mono text-[10px] font-medium bg-success/10 text-success rounded-full px-2 py-0.5">
                  170/250
                </span>
              </div>
              <div className="h-[5px] rounded-full bg-on-surface/[.08] overflow-hidden">
                <div className="w-[68%] h-full bg-success rounded-full" />
              </div>
              <div className="grid grid-cols-4 gap-2">
                {heroTiles.map((tile) => (
                  <div
                    key={tile.label}
                    className={`aspect-square rounded-lg checkerboard border border-outline-variant grid place-items-center font-mono text-[15px] font-bold ${tile.tone}`}
                  >
                    {tile.label}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 border border-success/30 bg-success/[.06] rounded-lg px-3 py-2 text-[11.5px] text-on-surface-variant">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="rgb(var(--success))"
                  strokeWidth="1.8"
                  className="shrink-0"
                >
                  <path d="M2 7.5L5.5 11 12 3.5" />
                </svg>
                {tf(
                  "marketing.landing.vignetteDone",
                  "4.320 Listing-Bilder als ZIP bereit — Feuerwehr Hoodies",
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Produktionsstraße */}
      <section className="border-t border-outline-variant">
        <div className="px-6 md:px-14 py-14 max-w-[1440px] mx-auto">
          <div className="section-label">
            {tf("marketing.landing.pipelineKicker", "So funktioniert es")}
          </div>
          <h2 className="text-[26px] lg:text-[30px] leading-[1.2] font-semibold tracking-[-.02em] text-on-surface mt-2.5 max-w-[560px]">
            {tf("marketing.landing.pipelineTitle", "Eine Produktionsstraße statt fünf Werkzeuge")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px mt-9 border border-outline-variant rounded-xl overflow-hidden bg-outline-variant">
            {steps.map((step) => (
              <div key={step.kicker} className="p-5 lg:p-[22px] bg-surface-container">
                <div className="font-mono text-[11px] font-semibold uppercase text-primary">
                  {step.kicker}
                </div>
                <div className="text-[14.5px] font-medium text-on-surface mt-2">{step.title}</div>
                <div className="text-xs leading-[1.55] text-on-surface-muted mt-1.5">
                  {step.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customy Desktop */}
      <section id="download" className="border-t border-outline-variant scroll-mt-16">
        <div className="grid lg:grid-cols-2 gap-10 items-center px-6 md:px-14 py-14 max-w-[1440px] mx-auto">
          <div>
            <div className="section-label">
              {tf("marketing.landing.desktopKicker", "Customy Desktop")}
            </div>
            <h2 className="text-[26px] lg:text-[30px] leading-[1.2] font-semibold tracking-[-.02em] text-on-surface mt-2.5">
              {tf("marketing.landing.desktopTitle", "Die App für den Druckplatz")}
            </h2>
            <p className="text-[15px] leading-relaxed text-on-surface-variant mt-3 max-w-[480px]">
              {tf(
                "marketing.landing.desktopDescription",
                "Druckdateien an den Textildrucker senden, Versandlabels drucken, ausliefern und scannen – direkt am Produktionsplatz.",
              )}
            </p>
          </div>
          <div className="border border-outline-variant bg-surface-container rounded-[14px] p-6 md:p-8">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
              <a
                href={DESKTOP_DOWNLOAD_URL}
                className="inline-flex items-center gap-2 bg-primary text-on-primary hover:bg-primary-fixed font-semibold px-5 py-3 rounded-lg text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <span className="material-symbols-outlined text-lg leading-none" aria-hidden="true">
                  download
                </span>
                {tf("marketing.landing.desktopDownload", "Für Windows herunterladen")}
              </a>
              {desktopVersion && (
                <span className="font-mono text-[13px] text-on-surface-variant">
                  {t("marketing.landing.desktopVersion", { version: desktopVersion })}
                </span>
              )}
            </div>
            <p className="text-xs text-on-surface-faint mt-4">
              {tf(
                "marketing.landing.desktopRequirements",
                "Windows 10/11 · aktualisiert sich nach der Installation selbst",
              )}
            </p>
            {/* Entfällt, sobald der Installer signiert ist. */}
            <p className="text-xs leading-relaxed text-on-surface-muted mt-4 pt-4 border-t border-outline-variant">
              {tf(
                "marketing.landing.desktopSmartScreen",
                "Windows warnt beim ersten Start einmal: Weitere Informationen → Trotzdem ausführen",
              )}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-14 pb-14 max-w-[1440px] mx-auto w-full">
        <div
          className="border border-primary/25 bg-surface-container rounded-[14px] px-6 py-10 md:p-[46px] text-center"
          style={{
            backgroundImage:
              "radial-gradient(600px 300px at 50% 0%, rgb(var(--primary) / 0.08), transparent 70%)",
          }}
        >
          <h2 className="text-[24px] md:text-[28px] leading-[1.2] font-semibold tracking-[-.02em] text-on-surface">
            {tf("marketing.landing.ctaTitle", "Die nächsten 250 Produkte macht die Maschine.")}
          </h2>
          <p className="text-sm leading-relaxed text-on-surface-variant mt-2.5">
            {tf("marketing.landing.ctaSubtitle", "Kostenlos testen — ohne Kreditkarte.")}
          </p>
          <div className="inline-flex mt-6">
            <Button size="lg" onClick={() => navigate("/register")}>
              {tf("marketing.landing.ctaButton", "Kostenlos starten")}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
